import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenIcon from "@mui/icons-material/FolderOpenOutlined";
import LayersIcon from "@mui/icons-material/Layers";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import StopIcon from "@mui/icons-material/Stop";

import ActionsMenu, { type ActionsMenuItem } from "components/botsManagement/ActionsMenu";
import MetaPill, { type MetaTone } from "components/botsManagement/MetaPill";
import SoftTag from "components/botsManagement/SoftTag";
import WorkspaceMetaList, {
  type WorkspaceMetaRow,
} from "components/botsManagement/WorkspaceMetaList";
import { botKindMeta } from "components/botsManagement/botKind";
import { DETAIL_CARD_SX, DETAIL_LABEL_SX } from "components/botsManagement/detailCard";
import {
  createCoalescer,
  splitEventConcerns,
} from "components/botsManagement/panelWsEvents";
import SaveSplitResultDialog from "components/botsManagement/split/SaveSplitResultDialog";
import SplitBusySlotsAlert from "components/botsManagement/split/SplitBusySlotsAlert";
import SplitJobDeleteDialog from "components/botsManagement/split/SplitJobDeleteDialog";
import SplitSourceSsnField from "components/botsManagement/split/SplitSourceSsnField";
import SplitSsnCheckAlert from "components/botsManagement/split/SplitSsnCheckAlert";
import SplitSsnUpdaterDialog from "components/botsManagement/split/SplitSsnUpdaterDialog";
import {
  SPLIT_PHASE_LABELS,
  apiErrorText,
  formatSplitDateTime,
  formatSplitInputs,
  isSplitJobActive,
  isSplitJobSaving,
  parseSplitConcurrencyError,
  splitJobActionPlan,
  splitJobNeedsPolling,
  splitOptionLines,
  splitProgressSummary,
  splitResultTag,
  splitStatusChip,
  type SplitAction,
  type SplitActionKey,
} from "components/botsManagement/split/splitJobUi";
import { splitJobPathForPayload } from "components/botsManagement/split/splitRoutes";
import { deriveSplitSsnState, splitSsnListTag } from "components/botsManagement/split/splitSsn";
import CopyButton from "components/common/CopyButton";
import ConfirmDialog from "components/common/ConfirmDialog";
import CustomScrollbars from "components/CustomScrollbars";
import JobLogsPanel from "components/pdfTools/JobLogsPanel";
import JobSummaryPanel from "components/pdfTools/JobSummaryPanel";
import JobWorkspacePanel from "components/pdfTools/JobWorkspacePanel";
import { formatBytes } from "components/preview/previewTypes";
import api from "features/api";
import { pushNotification } from "features/notificationSlice";
import { removePdfSplitJob, trackPdfSplitJob } from "features/pdfSplitJobsSlice";
import {
  useGetPdfJobQuery,
  useGetPdfToolsConfigQuery,
  useLazyGetPdfJobDownloadQuery,
  useRestartPdfJobMutation,
  useRunPdfJobSsnCheckMutation,
  useStopPdfJobMutation,
  type PdfActiveJobRef,
  type PdfJobDetail,
  type PdfWorkspaceSection,
} from "features/pdfTools/pdfTools";
import { useAppDispatch, useAppSelector } from "features/store";
import { botsManagementWs } from "features/ws/botsManagementWs";
import useAppNavigate from "hooks/useAppNavigate";
import useResponsive from "hooks/useResponsive";
import useViewAs, { CLIENT_VIEW_READ_ONLY_TOOLTIP } from "hooks/useViewAs";
import type { ChipColor } from "utils/botStatusColors";
import { buildDataStorageFolderMappingSeed } from "utils/dataStorageNavigation";
import { startDownloadViaHiddenFrame } from "utils/hiddenFrameDownload";
import { isBotsOperator } from "utils/roles";

type DetailTab = "logs" | "workspace" | "summary";
const DETAIL_TABS: DetailTab[] = ["logs", "workspace", "summary"];

/** Polled only while the socket is down and something on the page moves. */
const FALLBACK_POLL_MS = 2500;

const ACTION_ICONS: Record<SplitActionKey, ReactNode> = {
  stop: <StopIcon />,
  resume: <PlayArrowIcon />,
  start_over: <RestartAltIcon />,
  save: <SaveAltIcon />,
  download: <DownloadIcon />,
  view_in_storage: <FolderOpenIcon />,
  delete: <DeleteIcon />,
};

function chipTone(color: ChipColor): MetaTone {
  return color === "default" ? "neutral" : (color as MetaTone);
}

function progressBarColor(job: PdfJobDetail): "primary" | "success" | "error" | "warning" {
  if (job.status === "completed") return "success";
  if (job.status === "failed") return "error";
  if (job.status === "stopped" || job.status === "canceled") return "warning";
  return "primary";
}

/** The line under the bar: where a running split is, how a finished one ended. */
function progressDetail(job: PdfJobDetail): string {
  if (isSplitJobActive(job)) {
    const phase =
      SPLIT_PHASE_LABELS[job.progress.phase] ??
      (job.status === "pending" ? "Waiting to start" : "Working");
    return [phase, job.progress.current_file, job.progress.note].filter(Boolean).join(" - ");
  }
  if (job.status === "completed") {
    return `${splitProgressSummary(job).secondary} - finished ${formatSplitDateTime(job.completed_at)}`;
  }
  if (job.status === "failed") return "Failed";
  return "Stopped - Resume continues from where it stopped";
}

function SetupItem({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 0, ...(wide ? { gridColumn: "1 / -1" } : {}) }}>
      <Typography variant="overline" sx={DETAIL_LABEL_SX}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.25, minWidth: 0, fontSize: "0.875rem", overflowWrap: "anywhere" }}>
        {children}
      </Box>
    </Box>
  );
}

function PathLine({ path }: { path: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontSize: "0.8rem", minWidth: 0, overflowWrap: "anywhere" }}
      >
        {path}
      </Typography>
      <CopyButton value={path} label="Copy the path" />
    </Box>
  );
}

function FoldButton({
  open,
  onToggle,
  label,
}: {
  open: boolean;
  onToggle: () => void;
  label: [string, string];
}) {
  return (
    <Button
      size="small"
      onClick={onToggle}
      endIcon={
        <ExpandMoreIcon
          sx={{ transition: "transform 150ms", transform: open ? "rotate(180deg)" : "none" }}
        />
      }
      sx={{ flexShrink: 0, textTransform: "none" }}
      aria-expanded={open}
    >
      {open ? label[1] : label[0]}
    </Button>
  );
}

export type SplitJobWorkspaceProps = {
  jobId: string;
  userId: number;
  /** The client as the panel names them (the user list, the major job page). */
  userLabel: string;
  majorJobId: number;
  /** Return to the split major job. */
  onBack: () => void;
};

/**
 * One PDF split job of a split major job (CAL-6001): what it split, where it
 * is, its logs and files, and the way from review to the client's Data
 * Storage - then, for CAL-5987, whether the saved employee folders can be
 * linked to the vendor's SSN file. Laid out like the task page, so an operator
 * reads both the same way.
 */
export default function SplitJobWorkspace({
  jobId,
  userId,
  userLabel,
  majorJobId,
  onBack,
}: SplitJobWorkspaceProps) {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isCardMode: isPhone, isShort } = useResponsive();
  // A laptop window and a phone both run out of height long before the logs
  // get any: the settled facts fold away and open on request.
  const foldMeta = isPhone || isShort;
  const [metaOpen, setMetaOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const { profile, readOnly } = useViewAs();
  const canEdit = isBotsOperator(profile) && !readOnly;
  const readOnlyReason = canEdit
    ? null
    : readOnly
      ? CLIENT_VIEW_READ_ONLY_TOOLTIP
      : "Read-only access.";

  // Live updates arrive over the panel's socket; polling covers only the time
  // it is not delivering.
  const wsAccessToken = useAppSelector((state) => state.auth.accessToken);
  const [wsConnected, setWsConnected] = useState(() => botsManagementWs.isConnected());
  const [pollMs, setPollMs] = useState(0);
  const query = useGetPdfJobQuery(jobId, {
    pollingInterval: pollMs,
    refetchOnMountOrArgChange: true,
  });
  const job = query.data?.job_id === jobId ? query.data : undefined;
  const { refetch } = query;

  useEffect(() => {
    const token = wsAccessToken || localStorage.getItem("accessToken");
    if (token) botsManagementWs.connect(token);
    const unStatus = botsManagementWs.subscribeStatus(setWsConnected);
    // Status listeners hear transitions only; a socket already up when this
    // effect runs would otherwise leave the fallback polling forever.
    setWsConnected(botsManagementWs.isConnected());
    const coalescer = createCoalescer();
    const unEvents = botsManagementWs.subscribe((event) => {
      if (!splitEventConcerns(event, { jobId })) return;
      // The job and the SSN Updater dialog when it is open: both read the job.
      coalescer.schedule(
        "split-job",
        () => dispatch(api.util.invalidateTags([{ type: "PdfSplitJobs", id: jobId }])),
        400,
      );
    });
    return () => {
      unStatus();
      unEvents();
      coalescer.cancelAll();
    };
  }, [jobId, wsAccessToken, dispatch]);

  const needsPolling = job != null && splitJobNeedsPolling(job);
  useEffect(() => {
    setPollMs(!wsConnected && needsPolling ? FALLBACK_POLL_MS : 0);
  }, [wsConnected, needsPolling]);

  // A link of another major job (or an old one without the ids) lands on the
  // job's own address.
  useEffect(() => {
    if (!job?.major_job || job.user.id == null) return;
    if (job.major_job.id !== majorJobId || job.user.id !== userId) {
      navigate(splitJobPathForPayload(job), { replace: true });
    }
  }, [job, majorJobId, userId, navigate]);

  // The operator watched it finish here: no toast about it later.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (!job) return;
    if (isSplitJobActive(job)) {
      wasActiveRef.current = true;
      return;
    }
    if (wasActiveRef.current) {
      wasActiveRef.current = false;
      dispatch(removePdfSplitJob({ jobId: job.job_id }));
      dispatch(api.util.invalidateTags([{ type: "PdfSplitJobs", id: "LIST" }]));
    }
  }, [job, dispatch]);

  const { data: config } = useGetPdfToolsConfigQuery();
  const [stopJob] = useStopPdfJobMutation();
  const [restartJob] = useRestartPdfJobMutation();
  const [runSsnCheck, { isLoading: rechecking }] = useRunPdfJobSsnCheckMutation();
  const [triggerDownload] = useLazyGetPdfJobDownloadQuery();

  const [pendingAction, setPendingAction] = useState<SplitActionKey | null>(null);
  const [confirm, setConfirm] = useState<"stop" | "start_over" | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [updaterOpen, setUpdaterOpen] = useState(false);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busySlots, setBusySlots] = useState<{
    message: string;
    activeJobs: PdfActiveJobRef[];
  } | null>(null);
  // A remount of the Workspace tab opens it where the page points to.
  const [workspaceFocus, setWorkspaceFocus] = useState<{
    section: PdfWorkspaceSection;
    nonce: number;
  }>({ section: "result", nonce: 0 });

  const splitter = useMemo(
    () => config?.splitters.find((item) => item.key === job?.splitter_key) ?? null,
    [config, job?.splitter_key],
  );

  const tabParam = searchParams.get("tab") as DetailTab | null;
  const requestedTab = tabParam && DETAIL_TABS.includes(tabParam) ? tabParam : null;
  const hasSummary = Boolean(job?.summary);
  const tab: DetailTab =
    requestedTab === "summary" && !hasSummary
      ? "logs"
      : (requestedTab ?? (hasSummary ? "summary" : "logs"));
  const selectTab = (next: DetailTab) => {
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        params.set("tab", next);
        return params;
      },
      { replace: true },
    );
  };

  const refreshNow = async () => {
    setManualRefreshing(true);
    try {
      await refetch();
    } finally {
      setManualRefreshing(false);
    }
  };

  const header = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 1,
        flexShrink: 0,
      }}
    >
      <Typography variant="h6" fontWeight={700}>
        Split job
      </Typography>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ArrowBackIcon fontSize="small" />}
        onClick={onBack}
        sx={{ width: { xs: "100%", md: "auto" }, minHeight: { xs: 40, md: 0 } }}
      >
        Back to major job
      </Button>
    </Box>
  );

  const shell = (content: ReactNode) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minHeight: 0 }}>
      {header}
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }} trackGutter>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>{content}</Box>
      </CustomScrollbars>
    </Box>
  );

  if (!job) {
    const status = (query.error as { status?: number | string } | undefined)?.status;
    if (query.error) {
      return shell(
        <Alert
          severity={status === 404 ? "info" : "error"}
          action={
            status === 404 ? undefined : (
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            )
          }
        >
          {status === 404
            ? "This split job does not exist any more - it may have been deleted."
            : "The split job could not be loaded."}
        </Alert>,
      );
    }
    return shell(
      <Paper variant="outlined" sx={{ p: 1.5 }} aria-busy="true">
        <Skeleton variant="text" width="40%" height={28} />
        <Skeleton variant="rounded" height={44} sx={{ mt: 0.75 }} />
        <Skeleton variant="rounded" height={26} width="50%" sx={{ mt: 1.25 }} />
        <Skeleton variant="rounded" height={78} sx={{ mt: 1.25 }} />
      </Paper>,
    );
  }

  const vendor = job.major_job?.vendor ?? job.provider;
  const clientLabel = userLabel.trim() || job.user.username;
  const kindMeta = botKindMeta("split");
  const inputsLabel = formatSplitInputs(job.input_names, job.files_count);
  const metaRows: WorkspaceMetaRow[] = [
    ...(clientLabel ? [{ title: "User", value: clientLabel }] : []),
    { title: "Action", value: kindMeta.label, color: kindMeta.color },
    ...(vendor ? [{ title: "Vendor", value: vendor }] : []),
    ...(job.major_job ? [{ title: "Major job", value: job.major_job.name }] : []),
    ...(inputsLabel ? [{ title: "Input", value: inputsLabel }] : []),
  ];

  const statusChip = splitStatusChip(job.status);
  const resultTag = splitResultTag(job);
  const ssnState = deriveSplitSsnState(job);
  const ssnTag = splitSsnListTag(ssnState, job.ssn_check);
  const progress = splitProgressSummary(job);
  const active = isSplitJobActive(job);
  const saving = isSplitJobSaving(job);
  const errorPages = Number(job.totals.error_pages ?? 0);
  const detailLine = progressDetail(job);
  const optionLines = splitOptionLines(job.options, splitter);
  const saved = job.publish.status === "completed";
  const plan = splitJobActionPlan(job, { readOnlyReason, fold: foldMeta });

  const stop = async () => {
    setConfirm(null);
    setActionError(null);
    setPendingAction("stop");
    try {
      await stopJob(job.job_id).unwrap();
      dispatch(removePdfSplitJob({ jobId: job.job_id }));
      dispatch(
        pushNotification({
          message: "Split job stopped. Resume it or start over.",
          severity: "info",
        }),
      );
    } catch (err: unknown) {
      setActionError(apiErrorText(err, "The split job could not be stopped."));
    } finally {
      setPendingAction(null);
    }
  };

  const restart = async (mode: "resume" | "fresh") => {
    setConfirm(null);
    setActionError(null);
    setBusySlots(null);
    setPendingAction(mode === "resume" ? "resume" : "start_over");
    try {
      const restarted = await restartJob({ jobId: job.job_id, mode }).unwrap();
      dispatch(
        trackPdfSplitJob({
          jobId: restarted.job_id,
          label: `${restarted.user.username} - ${restarted.splitter_label}`,
          userId: restarted.user.id,
          majorJobId: restarted.major_job?.id ?? null,
        }),
      );
      dispatch(
        pushNotification({
          message:
            mode === "resume"
              ? "Resuming the split from where it stopped."
              : "Starting the split over.",
          severity: "info",
        }),
      );
    } catch (err: unknown) {
      const busy = parseSplitConcurrencyError(err);
      if (busy) setBusySlots({ message: busy.message, activeJobs: busy.activeJobs });
      else setActionError(apiErrorText(err, "The split job could not be restarted."));
    } finally {
      setPendingAction(null);
    }
  };

  const download = async () => {
    setActionError(null);
    setPendingAction("download");
    try {
      const archive = await triggerDownload(job.job_id).unwrap();
      startDownloadViaHiddenFrame(archive.download_url);
    } catch (err: unknown) {
      setActionError(apiErrorText(err, "The download could not start. Try again."));
    } finally {
      setPendingAction(null);
    }
  };

  // Data Storage opens at the saved folder, with the breadcrumbs a drill-down
  // would show; its header's Back button returns here.
  const viewInStorage = () => {
    const prefix = job.publish.destination.replace(/\/+$/, "");
    if (!prefix) return;
    const root = job.publish.folder_root;
    const folderMappingSeed =
      root?.prefix && root.display_name
        ? buildDataStorageFolderMappingSeed(root.display_name, root.prefix, prefix)
        : undefined;
    window.sessionStorage.setItem("botsReturnTo", `${location.pathname}${location.search}`);
    navigate("/data-storage", {
      state: {
        initialPrefix: prefix,
        initialFname: job.publish.folder_name,
        ...(folderMappingSeed ? { folderMappingSeed } : {}),
        fromBotsManagement: true,
      },
    });
  };

  const recheck = async () => {
    setActionError(null);
    try {
      await runSsnCheck(job.job_id).unwrap();
    } catch (err: unknown) {
      setActionError(apiErrorText(err, "The SSN check could not start."));
    }
  };

  const runAction = (key: SplitActionKey) => {
    if (key === "stop") setConfirm("stop");
    else if (key === "resume") void restart("resume");
    else if (key === "start_over") setConfirm("start_over");
    else if (key === "save") setSaveOpen(true);
    else if (key === "download") void download();
    else if (key === "view_in_storage") viewInStorage();
    else if (key === "delete") setDeleteOpen(true);
  };

  const actionButton = (action: SplitAction, index: number) => {
    const pending = pendingAction === action.key;
    return (
      <Tooltip key={action.key} title={action.disabledReason ?? ""}>
        <span>
          <Button
            size="small"
            variant={index === 0 && !action.danger ? "contained" : "outlined"}
            color={action.danger ? "error" : "primary"}
            startIcon={
              pending ? <CircularProgress size={14} color="inherit" /> : ACTION_ICONS[action.key]
            }
            disabled={action.disabledReason != null || pendingAction != null}
            onClick={() => runAction(action.key)}
          >
            {action.label}
          </Button>
        </span>
      </Tooltip>
    );
  };

  const moreActions: ActionsMenuItem[] = plan.more.map((action) => ({
    key: action.key,
    label: action.label,
    icon: ACTION_ICONS[action.key],
    hint: action.disabledReason ?? undefined,
    danger: action.danger,
    separatorBefore: action.key === "delete" && plan.more.length > 1,
    disabled: action.disabledReason != null || pendingAction != null,
    onClick: () => runAction(action.key),
  }));

  const reviewErrorPagesButton = (
    <Button
      color="inherit"
      size="small"
      onClick={() => {
        setWorkspaceFocus((previous) => ({
          section: "error_pages",
          nonce: previous.nonce + 1,
        }));
        selectTab("workspace");
      }}
    >
      Review
    </Button>
  );

  const refreshButton = (
    <Tooltip title="Refresh">
      <span>
        <IconButton
          size="small"
          color="primary"
          disabled={manualRefreshing}
          onClick={() => void refreshNow()}
          aria-label="Refresh the split job"
        >
          {manualRefreshing ? (
            <CircularProgress size={18} color="primary" />
          ) : (
            <ReplayIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );

  if (job.discarded_at) {
    return shell(
      <Alert severity="info">
        This split job was deleted {formatSplitDateTime(job.discarded_at)}. Its workspace is
        gone; a folder it saved to Data Storage stays unless it was deleted with it.
      </Alert>,
    );
  }

  return shell(
    <>
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {busySlots && (
        <SplitBusySlotsAlert
          message={busySlots.message}
          activeJobs={busySlots.activeJobs}
          note="Nothing was restarted. Try again when one of these finishes."
        />
      )}

      <Paper variant="outlined" sx={{ p: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            mb: 1,
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
            {/* Details drops under a long splitter name rather than breaking it. */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                columnGap: 0.5,
                minWidth: 0,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={800}
                sx={{ minWidth: 0, overflowWrap: "anywhere" }}
              >
                {job.splitter_label}
              </Typography>
              {foldMeta && (
                <FoldButton
                  open={metaOpen}
                  onToggle={() => setMetaOpen((previous) => !previous)}
                  label={["Details", "Details"]}
                />
              )}
            </Box>
            {foldMeta ? (
              metaOpen ? (
                <WorkspaceMetaList rows={metaRows} />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.25,
                    overflowWrap: "anywhere",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {metaRows.map((row) => row.value).join(" - ")}
                </Typography>
              )
            ) : (
              <Box
                sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "stretch", mt: 0.75 }}
              >
                {clientLabel && (
                  <MetaPill
                    title="User"
                    tone="primary"
                    icon={<PersonOutlineIcon />}
                    value={clientLabel}
                  />
                )}
                <MetaPill
                  title="Action"
                  tone={kindMeta.tone}
                  icon={kindMeta.icon}
                  value={kindMeta.label}
                />
                {vendor && (
                  <MetaPill title="Vendor" tone="secondary" icon={<BusinessIcon />} value={vendor} />
                )}
                {job.major_job && (
                  <MetaPill title="Major job" icon={<LayersIcon />} value={job.major_job.name} />
                )}
                {inputsLabel && (
                  <MetaPill title="Input" icon={<DescriptionIcon />} value={inputsLabel} />
                )}
              </Box>
            )}
          </Box>
          {refreshButton}
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
          <SoftTag
            tone={chipTone(statusChip.color)}
            label={`Status: ${statusChip.label}`}
            title="Where the split is"
          />
          {resultTag && (
            <SoftTag
              tone={resultTag.tone}
              label={resultTag.label}
              title="What became of the result"
            />
          )}
          {ssnTag && (
            <SoftTag
              tone={ssnTag.tone}
              label={ssnTag.label}
              title="The saved folders against the vendor's SSN file"
            />
          )}
        </Stack>

        <Box sx={{ ...DETAIL_CARD_SX, mb: 1.25 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 1,
              minWidth: 0,
              minHeight: 22,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              noWrap
              title={progress.primary}
              sx={{ minWidth: 0, fontVariantNumeric: "tabular-nums" }}
            >
              {progress.primary}
            </Typography>
            {progress.percent != null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
              >
                {Math.floor(progress.percent)}%
              </Typography>
            )}
          </Box>
          <LinearProgress
            variant={active && progress.percent == null ? "indeterminate" : "determinate"}
            value={progress.percent ?? 0}
            color={progressBarColor(job)}
            sx={{ height: 8, borderRadius: 1, my: 0.75 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={detailLine}
            sx={{ display: "block", minHeight: 20 }}
          >
            {detailLine}
          </Typography>
        </Box>

        {job.status === "failed" && (
          <Alert severity="error" sx={{ mb: 1.25 }}>
            {job.error_message ||
              "The split failed. The uploaded files are untouched - resume it or start it over."}
          </Alert>
        )}
        {(job.status === "stopped" || job.status === "canceled") && (
          <Alert severity="info" sx={{ mb: 1.25 }}>
            This split job was stopped. Everything it produced so far is kept: Resume
            continues from where it stopped, Start over runs it again from the beginning.
          </Alert>
        )}
        {job.status === "completed" && errorPages > 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 1.25, "& .MuiAlert-message": { minWidth: 0 } }}
            action={isPhone ? undefined : reviewErrorPagesButton}
          >
            {errorPages.toLocaleString()} page(s) could not be matched to an employee. They
            are in no employee folder - review them under Workspace, Error pages.
            {/* A phone keeps the text its width and puts the button under it. */}
            {isPhone && <Box sx={{ mt: 0.75 }}>{reviewErrorPagesButton}</Box>}
          </Alert>
        )}
        {saving && (
          <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 1.25 }}>
            Saving to Data Storage - copying the result into {job.publish.destination}
          </Alert>
        )}
        {job.publish.status === "failed" && (
          <Alert severity="error" sx={{ mb: 1.25 }}>
            The save did not finish: {job.publish.error || "unknown error"}. Save again to
            complete it.
          </Alert>
        )}
        {ssnState !== "hidden" && ssnState !== "waiting" && (
          <Box sx={{ mb: 1.25 }}>
            {ssnState !== "not_saved" && (
              <SplitSourceSsnField
                jobId={job.job_id}
                vendor={vendor}
                canEdit={canEdit}
                readOnlyReason={readOnlyReason}
                busy={ssnState === "checking" || ssnState === "updater_running"}
              />
            )}
            <SplitSsnCheckAlert
              job={job}
              vendor={vendor}
              canEdit={canEdit}
              readOnlyReason={readOnlyReason}
              rechecking={rechecking}
              onRecheck={() => void recheck()}
              onOpenUpdater={() => setUpdaterOpen(true)}
            />
          </Box>
        )}

        <Divider sx={{ mb: 1.25 }} />

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{
            "& > span": { width: { xs: "100%", md: "auto" } },
            "& .MuiButton-root": {
              width: { xs: "100%", md: "auto" },
              minHeight: { xs: 40, md: 0 },
            },
          }}
        >
          {plan.primary.map(actionButton)}
          <ActionsMenu label="More" ariaLabel="More split job actions" actions={moreActions} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
        >
          <Typography variant="overline" sx={DETAIL_LABEL_SX}>
            Setup
          </Typography>
          {foldMeta && (
            <FoldButton
              open={setupOpen}
              onToggle={() => setSetupOpen((previous) => !previous)}
              label={["Show", "Hide"]}
            />
          )}
        </Box>
        {(!foldMeta || setupOpen) && (
          <Box
            sx={{
              mt: 1,
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1.5,
            }}
          >
            <SetupItem label="Splitter">{job.splitter_label}</SetupItem>
            {optionLines.map((line) => (
              <SetupItem key={line.label} label={line.label}>
                {line.value}
              </SetupItem>
            ))}
            <SetupItem label="Created">
              {formatSplitDateTime(job.created_at)}
              {job.created_by.username ? ` by ${job.created_by.username}` : ""}
            </SetupItem>
            <SetupItem label="Started">{formatSplitDateTime(job.started_at)}</SetupItem>
            <SetupItem label="Finished">{formatSplitDateTime(job.completed_at)}</SetupItem>
            <SetupItem label="Input files" wide>
              {job.inputs.length
                ? job.inputs
                    .map((input) =>
                      input.size != null
                        ? `${input.file_name} (${formatBytes(input.size)})`
                        : input.file_name,
                    )
                    .join(", ")
                : "-"}
            </SetupItem>
            {job.publish.destination && (
              <SetupItem label={saved ? "Saved to" : "Saving to"} wide>
                <PathLine path={job.publish.destination} />
              </SetupItem>
            )}
            {saved && job.publish.upload_source_folders.length > 0 && (
              <SetupItem
                label={
                  job.publish.upload_source_folders.length > 1
                    ? "Source folders for upload jobs (one per form)"
                    : "Source folder for upload jobs"
                }
                wide
              >
                {job.publish.upload_source_folders.map((path) => (
                  <PathLine key={path} path={path} />
                ))}
              </SetupItem>
            )}
            <SetupItem label="Split job id" wide>
              <PathLine path={job.job_id} />
            </SetupItem>
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
        <Tabs
          value={tab}
          onChange={(_event, next: DetailTab) => selectTab(next)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab value="logs" label="Logs" />
          <Tab value="workspace" label="Workspace" />
          {hasSummary && <Tab value="summary" label="Summary" />}
        </Tabs>
        {tab === "logs" && <JobLogsPanel jobId={job.job_id} live={active} />}
        {tab === "workspace" && (
          <JobWorkspacePanel
            key={workspaceFocus.nonce}
            jobId={job.job_id}
            live={active}
            initialSection={workspaceFocus.section}
          />
        )}
        {tab === "summary" && hasSummary && <JobSummaryPanel job={job} />}
      </Paper>

      <ConfirmDialog
        open={confirm === "stop"}
        title="Stop this split job?"
        message="Processing pauses. Everything produced so far is kept, and Resume continues from the same place."
        confirmLabel="Stop"
        cancelLabel="Keep running"
        onConfirm={() => void stop()}
        onCancel={() => setConfirm(null)}
        loading={pendingAction === "stop"}
      />
      <ConfirmDialog
        open={confirm === "start_over"}
        title="Start this split job over?"
        message={
          saved
            ? "Its results, error pages and summary are cleared from the workspace (the uploaded files stay) and the split runs again from the beginning. The folder already saved to Data Storage stays as it is; saving the new result into it asks before overwriting."
            : "Its results, error pages and summary are cleared from the workspace (the uploaded files stay) and the split runs again from the beginning."
        }
        confirmLabel="Start over"
        cancelLabel="Keep"
        onConfirm={() => void restart("fresh")}
        onCancel={() => setConfirm(null)}
        loading={pendingAction === "start_over"}
      />
      <SaveSplitResultDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        job={job}
        vendor={vendor}
      />
      <SplitJobDeleteDialog
        job={deleteOpen ? job : null}
        onClose={() => setDeleteOpen(false)}
        onDeleted={onBack}
      />
      <SplitSsnUpdaterDialog
        jobId={updaterOpen ? job.job_id : null}
        jobName={`${job.splitter_label} - ${vendor}`}
        canEdit={canEdit}
        readOnlyReason={readOnlyReason}
        onClose={() => setUpdaterOpen(false)}
      />
    </>,
  );
}
